import { API } from "aws-amplify";
import { NextPage } from "next";
import router, { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { AuthGuard } from "src/components/authentication/auth-guard";
import ArticleEditorComponent from "src/components/dashboard/articleEditor";
import { DashboardLayout } from "src/components/dashboard/dashboard-layout";
import { getArticle, updateArticle } from "src/slices/articles/thunks";
import {
  selectIsCopyContent,
  selectIsDownloadContent,
  selectIsHumanizing,
  selectShowHumanizeContent,
  selectShowOriginalContent,
  setIsCopyContent,
  setIsDownloadContent,
} from "src/slices/humanize";
import { snackAdded } from "src/slices/system";
import { useDispatch, useSelector } from "src/store";

// Helper function to safely decode URL parameters
const safeDecodeURIComponent = (value: string | string[] | undefined): string => {
  if (!value) return "";
  const strValue = Array.isArray(value) ? value[0] : value;
  try {
    return decodeURIComponent(strValue);
  } catch (error) {
    console.error("Error decoding URI component:", error);
    return strValue;
  }
};

const ArticleEditor: NextPage = () => {
  const router = useRouter();
  const dispatch = useDispatch();
  
  // Wait for router to be ready before accessing query params
  const isRouterReady = router.isReady;
  
  // Safely get and decode parameters
  const id = isRouterReady ? (router.query.id as string) : "";
  const urlTitle = isRouterReady ? safeDecodeURIComponent(router.query.title) : "";
  const urlDescription = isRouterReady ? safeDecodeURIComponent(router.query.description) : "";

  // Bare Slack/Hermes article links carry only ?id= (no title/description), so the
  // Meta Title/Description panel would show the "Default Title"/"Default Description"
  // placeholders. Resolve the missing field(s) from the brief record — the same
  // source and shape the grid uses to build full links (ArticleLinkRenderer →
  // GET /briefs/object/{id}). The editor's `id` is the brief id (getArticle sends
  // it as briefId), so this endpoint resolves with it.
  // The fetched meta is tagged with the id it belongs to so a client-side nav to
  // another bare article never shows the previous article's meta (state persists
  // across route changes); we only apply it when it matches the current id.
  const [briefMeta, setBriefMeta] = useState<{ id: string; title: string; description: string } | null>(null);
  useEffect(() => {
    // Fetch when EITHER field is absent (a URL-supplied field still wins below).
    if (!isRouterReady || !id || (urlTitle && urlDescription)) return;
    let cancelled = false;
    (async () => {
      try {
        const response: any = await API.get("brief", `/briefs/object/${id}`, {});
        const brief = response?.data || response;
        if (!cancelled && brief && !brief.error) {
          setBriefMeta({
            id,
            title: brief.title || "",
            description: brief.description || "",
          });
        }
      } catch (error) {
        console.error("Failed to resolve article meta from brief:", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isRouterReady, id, urlTitle, urlDescription]);

  const resolvedMeta = briefMeta?.id === id ? briefMeta : null;
  const title = urlTitle || resolvedMeta?.title || "Default Title";
  const description = urlDescription || resolvedMeta?.description || "Default Description";
  const hideArticle = isRouterReady ? (router.query.hideArticle === "true") : false;
  const isOptimizedMode = isRouterReady ? (router.query.isOptimizedEditor === "true") : false;

  // Get state from Redux if in regular mode
  const isHumanizing = useSelector(selectIsHumanizing);
  const showHumanizeContent = useSelector(selectShowHumanizeContent);
  const showOriginalContent = useSelector(selectShowOriginalContent);
  const isCopyContent = useSelector(selectIsCopyContent);
  const isDownloadContent = useSelector(selectIsDownloadContent);

  // Fetch content function
  const fetchContent = async (id: string) => {
    try {
      if (!id) {
        dispatch(snackAdded({
          message: "Invalid article ID",
          type: "error",
          duration: 3000,
        }));
        return null;
      }
      
      const payload = await dispatch(getArticle({ id })).unwrap();
      if (payload?.status === 'error') {
        dispatch(snackAdded({
          message: payload.error || 'Failed to load content',
          type: "error",
          duration: 3000,
        }));
        return null;
      }
      return payload?.data;
    } catch (error) {
      console.error('Error fetching article content:', error);
      dispatch(snackAdded({
        message: 'Failed to load content. Please try again.',
        type: "error",
        duration: 3000,
      }));
      return null;
    }
  };

  // Save content function
  const saveContent = async (content: string, options: any) => {
    try {
      return await dispatch(
        updateArticle({
          id,
          content,
          isHumanized: options?.isHumanized,
          isEdited: options?.isEdited,
          charCount: options?.charCount,
          lastKnownVersion: options?.lastKnownVersion,
        })
      ).unwrap();
    } catch (error) {
      console.error('Error saving article content:', error);
      dispatch(snackAdded({
        message: 'Failed to save content. Please try again.',
        type: "error",
        duration: 3000,
      }));
      return null;
    }
  };

  return (
    <ArticleEditorComponent
      id={id}
      title={title}
      description={description}
      hideArticle={hideArticle}
      isOptimizedMode={false}
      fetchContent={fetchContent}
      saveContent={saveContent}
      additionalProps={{
        isHumanizing,
        showHumanizeContent,
        showOriginalContent,
        isCopyContent,
        isDownloadContent,
        onHumanize: async (content: string) => {},
        setIsCopyContent: (value) => dispatch(setIsCopyContent(value)),
        setIsDownloadContent: (value) => dispatch(setIsDownloadContent(value)),
      }}
    />
  );
};

ArticleEditor.getLayout = (page) => {
  return (
    <AuthGuard>
      <DashboardLayout
        isArticleEditor={true}
        title={safeDecodeURIComponent(router.query.title) || ""}
        id={(router.query.id as string) || ""}
        description={safeDecodeURIComponent(router.query.description) || ""}
        isHideArticle={router.query.hideArticle as string}
        isOptimizedEditor={false}
      >
        {page}
      </DashboardLayout>
    </AuthGuard>
  );
};

export default ArticleEditor;
